import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const FacilityContext = createContext();

export const useFacility = () => {
  const context = useContext(FacilityContext);
  if (!context) {
    throw new Error('useFacility must be used within a FacilityProvider');
  }
  return context;
};

export const FacilityProvider = ({ children }) => {
  const { user } = useAuth();
  const [activeFacilityId, setActiveFacilityIdState] = useState(null);
  const [allowedFacilities, setAllowedFacilities] = useState([]);

  useEffect(() => {
    if (!user) {
      setAllowedFacilities([]);
      setActiveFacilityIdState(null);
      localStorage.removeItem('active_facility_id');
      return;
    }

    const facilities = user.allowedFacilities || [];
    setAllowedFacilities(facilities);

    // Restore saved facility or pick default
    const saved = localStorage.getItem('active_facility_id');
    const savedId = saved ? parseInt(saved, 10) : null;

    const facilityIdOf = (f) => {
      // Backend uses `facility_id`; in case of older payloads we accept `id` too.
      const v = f?.facility_id ?? f?.id ?? f?.facilityId;
      return typeof v === 'string' ? parseInt(v, 10) : v;
    };

    const hasSaved = savedId && facilities.some(f => {
      const fid = facilityIdOf(f);
      return fid != null && Number(fid) === Number(savedId);
    });

    if (hasSaved) {
      setActiveFacilityIdState(savedId);
    } else {
      // Clear stale localStorage value to avoid sending unauthorized `X-Facility-Id`.
      if (savedId) localStorage.removeItem('active_facility_id');
      const defaultFac = facilities.find(f => f.is_default) || facilities[0];
      const id = facilityIdOf(defaultFac) ?? null;
      setActiveFacilityIdState(id);
      if (id) {
        localStorage.setItem('active_facility_id', String(id));
      }
    }
  }, [user]);

  const setActiveFacilityId = useCallback((id) => {
    setActiveFacilityIdState(id);
    if (id) {
      localStorage.setItem('active_facility_id', String(id));
    } else {
      localStorage.removeItem('active_facility_id');
    }
  }, []);

  // Call this after saving Ground Location so header + weather widget update immediately
  // without requiring a logout/login cycle.
  const updateActiveFacilityLocation = useCallback((locationData) => {
    setAllowedFacilities(prev => prev.map(f => {
      if (Number(f.facility_id) !== Number(activeFacilityId)) return f;
      return {
        ...f,
        latitude:  locationData.latitude  ?? f.latitude,
        longitude: locationData.longitude ?? f.longitude,
        city:      locationData.city      ?? f.city,
        state:     locationData.state     ?? f.state,
        address:   locationData.address   ?? f.address,
      };
    }));
    // Clear weather cache so the widget re-fetches with new coordinates
    try {
      sessionStorage.removeItem(`weather_cache_${activeFacilityId}`);
    } catch { /* ignore */ }
  }, [activeFacilityId]);

  const activeFacility = allowedFacilities.find(
    (f) => Number(f.facility_id) === Number(activeFacilityId)
  ) || null;
  const hasMultipleFacilities = allowedFacilities.length > 1;
  // Derived for convenience — consumers can read facilityStatus directly instead of drilling activeFacility.status
  const facilityStatus = activeFacility?.status ?? null;

  const value = {
    activeFacilityId,
    activeFacility,
    allowedFacilities,
    hasMultipleFacilities,
    facilityStatus,
    setActiveFacilityId,
    updateActiveFacilityLocation,
  };

  return (
    <FacilityContext.Provider value={value}>
      {children}
    </FacilityContext.Provider>
  );
};
